import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useWindowDimensions, useColorScheme, StyleSheet, Text, View, Pressable} from 'react-native';
import {ProgressBar} from 'interface/base/ProgressBar';
import {IconButton} from 'interface/base/IconButton';
import {getDetails, getTimeStamp} from 'utils/mediafire';
import {colors, breakpoints, vectors} from 'config/styles';
import {theme} from 'config/themes';
import {isWeb} from 'utils/platform';

import type {ReactNode, MouseEvent} from 'react';
import type {GestureResponderEvent} from 'react-native';
import type {FileListItemProps} from 'interface/stacks/FileListItem';

const IS_WEB = isWeb();

export interface FileListItemRowProps extends FileListItemProps {
  menuAnchor?: any,
  renderIcons?: () => ReactNode,
  renderThumbnail?: () => ReactNode,
  onDropDownMenu?: (e: MouseEvent | GestureResponderEvent) => void,
  onTitlePress?: (e: GestureResponderEvent) => void,
  onSelectThumbnail?: (e: MouseEvent | GestureResponderEvent) => void,
  onShare?: (e: MouseEvent | GestureResponderEvent) => void,
  onCopyLink?: (e: MouseEvent | GestureResponderEvent) => void,
}

export function FileListItemRow(props: FileListItemRowProps) {
  const {t} = useLingui();
  const screen = useWindowDimensions();
  const scheme = useColorScheme();
  const limited = props.rootFolder === 'following';
  const isUpload = Boolean(props.item.uploadStatus);
  const hasColumns = screen.width >= breakpoints.columns && !isUpload && !props.hideIcons;
  const isSelected = props.selectItems?.includes(props.item.id);
  const hasSelection = props.selectItems?.length > 0;
  const hasSelectBox = hasSelection;
  const hasProgress = Boolean(props.item.uploadStatus);
  const hasIcons = !hasProgress && !props.hideIcons;
  const hasExtraIcons = hasIcons && !props.hideExtraIcons;
  const hasSelectIcon = hasIcons && (hasSelectBox || isSelected);
  const hasMenuIcon = hasIcons && !hasSelectIcon;
  const hasCancelIcon = false;
  const classes = {
    text: [
      styles.text,
      isSelected && styles.textSelected,
    ],
  };

  return (
    <View style={styles.root}>
      <View style={styles.thumbnail}>
        <Pressable onPress={props?.onSelectThumbnail}>
          {props.renderThumbnail()}
        </Pressable>
      </View>
      <View style={[styles.inner, hasColumns && styles.innerColumns]}>
        <Text
          style={styles.title}
          ellipsizeMode="middle"
          lineBreakMode="middle"
          numberOfLines={1}
          onPress={props.disableOpen ? undefined : props.onTitlePress}>
          {IS_WEB && !props.disableOpen
            ? <span className="text-link">
                {props.item.name}
              </span>
            : props.item.name
          }
        </Text>
        <Text
          style={classes.text}
          numberOfLines={1}
          ellipsizeMode="tail">
          {getDetails(props.item, limited)}
        </Text>
      </View>
      {/* Fill space if we have columns */}
      {hasColumns && <View style={styles.full}/>}
      <View style={styles.info}>
        {hasExtraIcons && props.renderIcons()}
        {hasProgress && <>
          {props.item.url && <View style={styles.textActions}>
            <Text onPress={props.onShare} numberOfLines={1} style={styles.textAction}>
              {IS_WEB ? <span className="text-link">{t`Share`}</span> : t`Share`}
            </Text>
            <Text onPress={props.onCopyLink} numberOfLines={1} style={styles.textAction}>
              {IS_WEB ? <span className="text-link">{t`Copy Link`}</span> : t`Copy Link`}
            </Text>
          </View>}
          <View style={styles.progress}>
            {props.item.uploadStatus !== 'Completed' && !props.item.uploadFailure
              ? <ProgressBar
                  style={{width: 100}}
                  items={[
                    {value: (props.item.uploadHashed / props.item.size) * 100, color: theme.transfers.progress.hashed},
                    {value: (props.item.uploadProgress / props.item.size) * 100, color: theme.transfers.progress.uploaded},
                  ]}
                />
              : <View style={styles.progressText}>
                  <Text style={[styles.progressStatus, props.item.uploadFailure && styles.progressError]}>
                    {props.item.uploadStatus}
                  </Text>
                </View>
            }
          </View>
        </>}
        {hasColumns &&
          <View style={styles.columns}>
            <View style={styles.column}>
              <Text numberOfLines={1} style={classes.text}>
                {getTimeStamp(props.item.created)}
              </Text>
            </View>
          </View>
        }
        {hasMenuIcon &&
          <View style={styles.action}>
            <IconButton
              reference={props.menuAnchor}
              title={t`Open context menu`}
              onPress={props.onDropDownMenu}
              buttonStyle={styles.actionButton}
              icon={
                <Svg viewBox={'0 0 6 16'} width={8} height={14}>
                  <Path d={vectors.icons.more} fill={colors.neutral.mid}/>
                </Svg>
              }
              iconHover={
                <Svg viewBox={'0 0 6 16'} width={8} height={14}>
                  <Path d={vectors.icons.more} fill={scheme === 'dark' ? colors.neutral.white : colors.neutral.black}/>
                </Svg>
              }
            />
          </View>
        }
        {hasCancelIcon &&
          <View style={styles.action}>
            <IconButton
              title={t`Cancel upload`}
              buttonStyle={styles.actionButton}
              icon={
                <Svg viewBox={'0 0 6 16'} width={8} height={14}>
                  <Path d={vectors.icons.close} fill={colors.neutral.mid}/>
                </Svg>
              }
              iconHover={
                <Svg viewBox={'0 0 6 16'} width={8} height={14}>
                  <Path d={vectors.icons.close} fill={scheme === 'dark' ? colors.neutral.white : colors.neutral.black}/>
                </Svg>
              }
            />
          </View>
        }
        {hasSelectIcon &&
          <View style={styles.action}>
            <IconButton
              title={t`Toggle select`}
              onPress={props?.onSelectThumbnail}
              buttonStyle={styles.actionButton}
              icon={
                <Svg viewBox={'0 0 24 24'} width={20} height={20}>
                  <Path
                    d={isSelected ? vectors.icons.boxChecked : vectors.icons.boxBlank}
                    fill={isSelected ? theme.files.listItem.selectedCheck : colors.neutral.mid}
                  />
                </Svg>
              }
              iconHover={
                <Svg viewBox={'0 0 24 24'} width={20} height={20}>
                  <Path
                    d={isSelected ? vectors.icons.boxChecked : vectors.icons.boxBlank}
                    fill={scheme === 'dark' ? colors.neutral.white : colors.neutral.black}
                  />
                </Svg>
              }
            />
          </View>
        }
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: 100,
    flexDirection: 'row',
  },
  thumbnail: {
    width: 56,
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },
  inner: {
    flexGrow: 1,
    flexShrink: 1,
    overflow: 'hidden',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  innerColumns: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexGrow: 0,
  },
  info: {
    flexGrow: 0,
    flexShrink: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    color: theme.files.listItem.title,
    fontSize: 13,
    marginRight: 8,
    marginBottom: 1,
  },
  progress: {
    flex: 1,
    justifyContent: "center",
    alignItems: 'flex-end',
    marginRight: 20,
  },
  progressText: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  progressStatus: {
    fontSize: 12,
    color: theme.files.listItem.title,
  },
  progressError: {
    color: colors.secondary.redOrange,
  },
  columns: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
  },
  column: {
    flex: 1,
    justifyContent: 'center',
  },
  action: {
    flex: 0,
    minWidth: 70,
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },
  actionButton: {
    width: 54,
    minWidth: 54,
  },
  date: {
    flex: 1,
    alignItems: 'flex-end',
    justifyContent: 'center',
    marginRight: 20,
  },
  text: {
    color: colors.neutral.darkgrey,
    fontSize: 11,
  },
  textSelected: {
    color: theme.files.listItem.selectedText,
  },
  textActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 48,
    flexShrink: 0,
    flexGrow: 0,
    gap: 16,
  },
  textAction: {
    color: colors.primary.brand,
    fontSize: 12,
  },
  full: {
    flex: 1,
  },
});
